import React, { useState, useEffect } from 'react';
import {
Table,
TableBody,
TableCell,
TableContainer,
TableHead,
TableRow,
Paper,
TextField,
Button,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Tooltip,
Grid,
Typography,
Pagination,
Slide,
Fade,
InputAdornment,
Snackbar,
Alert,
Box,
CircularProgress,
Chip,
Select,
MenuItem,
FormControl,
InputLabel,
} from '@mui/material';
import {
Add as AddIcon,
Edit as EditIcon,
Delete as DeleteIcon,
Search as SearchIcon,
Refresh as RefreshIcon,
Visibility as ViewIcon,
CheckCircle as ApproveIcon,
Cancel as RejectIcon,
Pending as PendingIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TEXT_CONNECTION_ERROR } from '../../utilities/Text';
import { APILPOList, APILPOShow, APILPOUpdate, APILPODelete, APILPOStatus, headers } from '../../utilities/APIs';
import useCustomToast from '../../hooks/useToast';
import axios from 'axios';
import { formatCreatedAtDate, formatNumberWithComma } from '../../utilities/Functions';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../utilities/Permission';

const Orders = () => {
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

// State
const [loading, setLoading] = useState(true);
const [openView, setOpenView] = useState(false);
const [openEdit, setOpenEdit] = useState(false);
const [openDelete, setOpenDelete] = useState(false);
const [openStatus, setOpenStatus] = useState(false);
const [currentOrder, setCurrentOrder] = useState(null);
const [page, setPage] = useState(1);
const [rowsPerPage, setRowsPerPage] = useState(10);
const [searchTerm, setSearchTerm] = useState('');
const [error, setError] = useState(null);
const [updateId, setUpdateId] = useState('');
const [statusFilter, setStatusFilter] = useState('all');
const showToast = useCustomToast();
const [pageState, setPageState] = useState({
    pageData: [],
});
const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
});

// Form data for status update
const [statusFormData, setStatusFormData] = useState({
    status: ''
});

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

// Helper function
const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

useEffect(() => {
    fetchData();
}, []);

const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
    const response = await axios.get(APILPOList, { headers });
    const data = Array.isArray(response.data.data) ? response.data.data.reverse() : [];
    
    setPageState(prev => ({
        ...prev,
        pageData: data,
    }));

    } catch (error) {
    let errorMessage = "Failed to load orders data";

    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    }
    else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
    }

    setError(errorMessage);
    console.log(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

// Filter orders based on search term and status
const filteredOrders = pageState.pageData.filter(order => {
    const matchesSearch = 
    order.lpo_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.buyer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.buyer_country.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.requisitioner_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (order.supplier_vendor_name && order.supplier_vendor_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (order.supplier_country && order.supplier_country.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    
    return matchesSearch && matchesStatus;
});

// Pagination
const count = Math.ceil(filteredOrders.length / rowsPerPage);
const indexOfLastRow = page * rowsPerPage;
const indexOfFirstRow = indexOfLastRow - rowsPerPage;
const currentRows = filteredOrders.slice(indexOfFirstRow, indexOfLastRow);

const handleChangePage = (event, newPage) => {
    setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
};

// Dialog handlers
const handleOpenView = (order) => {
    setCurrentOrder(order);
    setOpenView(true);
};

const handleOpenEdit = (order) => {
    setCurrentOrder(order);
    setUpdateId(order.id);
    setOpenEdit(true);
};

const handleOpenDelete = (order) => {
    setCurrentOrder(order);
    setUpdateId(order.id);
    setOpenDelete(true);
};

const handleOpenStatus = (order) => {
    setCurrentOrder(order);
    setUpdateId(order.id);
    setStatusFormData({
        status: order.status || 'pending'
    });
    setOpenStatus(true);
};

const handleClose = () => {
    setOpenView(false);
    setOpenEdit(false);
    setOpenDelete(false);
    setOpenStatus(false);
    setCurrentOrder(null);
};

const handleRefresh = () => {
    setLoading(true);
    fetchData();
    setSnackbar({ open: true, message: 'Orders data refreshed', severity: 'info' });
};

const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
};

// Status update handler
const handleStatusChange = (e) => {
    const { name, value } = e.target;
    setStatusFormData(prev => ({
        ...prev,
        [name]: value
    }));
};

// Update order status
const handleStatusUpdate = async () => {
    try {
        const formDataToSend = new FormData();
        formDataToSend.append('status', statusFormData.status);

        const response = await axios.post(`${APILPOStatus}/${updateId}`, formDataToSend, { headers });

        if (response.data.status === "success") {
            handleClose();
            fetchData();
            showToast("Success", `Order status updated to ${statusFormData.status}`, "success");
        } else {
            showToast("Error", "Failed to update status", "error");
        }
    } catch (error) {
        if (error.response) {
            if (error.response.status === 422) {
                const errors = error.response.data.errors;
                let errorMessages = [];
                for (const field in errors) {
                    errorMessages.push(...errors[field]);
                }
                showToast("Validation Error", errorMessages.join("\n"), "error");
            } else {
                showToast("Error", error.response.data.message || "An error occurred", "error");
            }
        } else if (error.code === "ERR_NETWORK") {
            showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
        } else {
            showToast("Error", "An unexpected error occurred", "error");
        }
    }
};

// Delete order
const handleDelete = async () => {
    try {
        const formDataToSend = new FormData();
        formDataToSend.append('updateId', updateId);

        const response = await axios.post(APILPODelete, formDataToSend, { headers });

        if (response.data.status === "success") {
            handleClose();
            fetchData();
            showToast("Success", "Order deleted successfully", "success");
        } else {
            showToast("Error", "Failed to delete order", "error");
        }
    } catch (error) {
        if (error.response) {
            if (error.response.status === 422) {
                const errors = error.response.data.errors;
                let errorMessages = [];
                for (const field in errors) {
                    errorMessages.push(...errors[field]);
                }
                showToast("Validation Error", errorMessages.join("\n"), "error");
            } else {
                showToast("Error", error.response.data.message || "An error occurred", "error");
            }
        } else if (error.code === "ERR_NETWORK") {
            showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
        } else {
            showToast("Error", "An unexpected error occurred", "error");
        }
    }
};

// Get status chip color
const getStatusColor = (status) => {
    switch(status) {
        case 'pending':
            return 'warning';
        case 'approved':
            return 'success';
        case 'rejected':
            return 'error';
        case 'completed':
            return 'info';
        default:
            return 'default';
    }
};

// Loading component
if (loading) {
    return (
    <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="200px"
        flexDirection="column"
        gap={2}
    >
        <CircularProgress />
        <Typography variant="body1">Loading orders...</Typography>
    </Box>
    );
}

// Error component
if (error) {
    return (
    <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="200px"
        flexDirection="column"
        gap={2}
    >
        <Typography variant="h6" color="error">
        Error Loading Data
        </Typography>
        <Typography variant="body2" color="textSecondary">
        {error}
        </Typography>
        <Button 
        variant="contained" 
        color="primary" 
        onClick={fetchData}
        startIcon={<RefreshIcon />}
        >
        Retry
        </Button>
    </Box>
    );
}

return (
    <div style={{ marginTop: 15, padding: isMobile ? '8px' : '24px' }}>
    <Slide direction="down" in={true} mountOnEnter unmountOnExit>
        <Paper elevation={3} style={{
        padding: isMobile ? '16px' : '24px',
        borderRadius: '12px',
        marginBottom: '24px'
        }}>
        <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item>
            <Typography variant="subtitle1">
                Orders Total:
                <Chip
                label={formatNumberWithComma(pageState.pageData.length)} 
                color="primary" 
                variant="outlined" 
                style={{ marginLeft: '8px' }}
                />
            </Typography>
            </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: '8px' }}>
            <Grid item xs={12} sm={4} md={4}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search orders..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setPage(1);
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        style: { borderRadius: '8px' }
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
                <FormControl fullWidth variant="outlined">
                    <InputLabel>Status Filter</InputLabel>
                    <Select
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(1);
                        }}
                        label="Status Filter"
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
                <TextField
                    fullWidth
                    select
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    variant="outlined"
                    style={{ borderRadius: '8px' }}
                    SelectProps={{
                        native: true,
                    }}
                >
                    <option value={5}>5 per page</option>
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                </TextField>
            </Grid>
            <Grid item xs={12} md={12} style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <Tooltip title="Refresh data">
                    <IconButton
                        onClick={handleRefresh}
                        style={{ backgroundColor: theme.palette.grey[200], borderRadius: '8px' }}
                    >
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Grid>
        </Grid>
        </Paper>
    </Slide>

    <Fade in={!loading} timeout={500}>
        <div>
        <TableContainer component={Paper} elevation={3} style={{ borderRadius: '12px', marginBottom: '24px' }}>
            <Table>
            <TableHead>
                <TableRow style={{ backgroundColor: theme.palette.primary.main }}>
                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>#</TableCell>
                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>LPO Number</TableCell>
                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Buyer</TableCell>
                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Vendor</TableCell>
                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Requisitioner</TableCell>
                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Total (USD)</TableCell>
                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {currentRows.length > 0 ? (
                currentRows.map((order, index) => (
                    <StyledTableRow key={order.id}>
                        <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                                {order.lpo_number}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2">
                                {order.buyer_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {order.buyer_country}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2">
                                {order.supplier_vendor_name || '-'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {order.supplier_country}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2">
                                {order.requisitioner_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {order.requisitioner_area}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                                ${formatNumberWithComma(order.total_usd)}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Chip
                                label={order.status.toUpperCase()}
                                color={getStatusColor(order.status)}
                                size="small"
                            />
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2">
                                {formatCreatedAtDate(order.date)}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                <Tooltip title="View Details">
                                    <ActionButton
                                        variant="contained"
                                        color="info"
                                        size="small"
                                        onClick={() => handleOpenView(order)}
                                        style={{ backgroundColor: '#17a2b8' }}
                                    >
                                        <ViewIcon fontSize="small" />
                                    </ActionButton>
                                </Tooltip>
                                <Tooltip title="Update Status">
                                    <ActionButton
                                        variant="contained"
                                        color="warning"
                                        size="small"
                                        onClick={() => handleOpenStatus(order)}
                                    >
                                        <PendingIcon fontSize="small" />
                                    </ActionButton>
                                </Tooltip>
                                {hasPermission(PERMISSIONS.ALL) && (
                                    <>
                                        {/* <Tooltip title="Edit">
                                            <ActionButton
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={() => handleOpenEdit(order)}
                                                disabled={order.status === 'approved' || order.status === 'completed'}
                                            >
                                                <EditIcon fontSize="small" />
                                            </ActionButton>
                                        </Tooltip> */}
                                        <Tooltip title="Delete">
                                            <ActionButton
                                                variant="contained"
                                                color="error"
                                                size="small"
                                                onClick={() => handleOpenDelete(order)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </ActionButton>
                                        </Tooltip>
                                    </>
                                )}
                            </div>
                        </TableCell>
                    </StyledTableRow>
                ))
                ) : (
                <TableRow>
                    <TableCell colSpan={9} style={{ textAlign: 'center', padding: '40px' }}>
                    <Typography variant="subtitle1">
                        {searchTerm || statusFilter !== 'all' ? 'No matching orders found' : 'No orders available'}
                    </Typography>
                    </TableCell>
                </TableRow>
                )}
            </TableBody>
            </Table>
        </TableContainer>

        {filteredOrders.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <Pagination
                count={count}
                page={page}
                onChange={handleChangePage}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
            />
            </div>
        )}
        </div>
    </Fade>

    {/* View Order Dialog */}
    <Dialog
        open={openView}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>Order Details</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
            {currentOrder && (
                <Box>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                LPO Number: {currentOrder.lpo_number}
                            </Typography>
                            <Chip
                                label={currentOrder.status.toUpperCase()}
                                color={getStatusColor(currentOrder.status)}
                            />
                        </Box>
                    </Grid><br/>
                    
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="primary">Buyer Details</Typography>
                            <Typography variant="body2"><strong>Name:</strong> {currentOrder.buyer_name}</Typography>
                            <Typography variant="body2"><strong>Country:</strong> {currentOrder.buyer_country}</Typography>
                            <Typography variant="body2"><strong>Destination:</strong> {currentOrder.buyer_destination}</Typography>
                            <Typography variant="body2"><strong>Contact:</strong> {currentOrder.buyer_contact}</Typography>
                            <Typography variant="body2"><strong>Email:</strong> {currentOrder.buyer_email || 'N/A'}</Typography>
                        </Paper>
                    </Grid><br/>

                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="primary">Supplier Details</Typography>
                            <Typography variant="body2"><strong>Country:</strong> {currentOrder.supplier_country}</Typography>
                            <Typography variant="body2"><strong>Vendor Name:</strong> {currentOrder.supplier_vendor_name || 'N/A'}</Typography>
                            <Typography variant="body2"><strong>Contact Person:</strong> {currentOrder.supplier_contact_person}</Typography>
                            <Typography variant="body2"><strong>Account Name:</strong> {currentOrder.supplier_account_name || 'N/A'}</Typography>
                            <Typography variant="body2"><strong>Account No:</strong> {currentOrder.supplier_account_no || 'N/A'}</Typography>
                        </Paper>
                    </Grid><br/>

                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="primary">Logistics Details</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Typography variant="body2"><strong>Requisitioner:</strong> {currentOrder.requisitioner_name}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body2"><strong>Carriage:</strong> {currentOrder.carriage}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body2"><strong>Incoterm:</strong> {currentOrder.incoterm}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2"><strong>Airport:</strong> {currentOrder.airport || 'N/A'}</Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid><br/>

                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="primary">Items</Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Commodity ID</strong></TableCell>
                                        <TableCell><strong>Description</strong></TableCell>
                                        <TableCell><strong>Grade</strong></TableCell>
                                        <TableCell><strong>QTY</strong></TableCell>
                                        <TableCell><strong>Unit Price</strong></TableCell>
                                        <TableCell><strong>Total</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {currentOrder.items && JSON.parse(currentOrder.items).map((item, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{item.commodityId}</TableCell>
                                            <TableCell>{item.description}</TableCell>
                                            <TableCell>{item.grade}</TableCell>
                                            <TableCell>{item.quantity}</TableCell>
                                            <TableCell>${item.unitPrice}</TableCell>
                                            <TableCell>${(item.quantity * item.unitPrice).toFixed(2)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Grid>

                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="primary">Financial Summary</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2"><strong>Subtotal:</strong> USD {formatNumberWithComma(currentOrder.subtotal)}</Typography>
                                    <Typography variant="body2"><strong>Tax:</strong> USD {formatNumberWithComma(currentOrder.tax)}</Typography>
                                    <Typography variant="body2"><strong>Shipping:</strong> USD {formatNumberWithComma(currentOrder.shipping)}</Typography>
                                    <Typography variant="body2"><strong>Other:</strong> USD {formatNumberWithComma(currentOrder.other)}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1" fontWeight="bold"><strong>Total (USD):</strong> USD {formatNumberWithComma(currentOrder.total_ugx)}</Typography>
                                    <Typography variant="h6" color="primary"><strong>Total (USD):</strong> ${formatNumberWithComma(currentOrder.total_usd)}</Typography>
                                    <Typography variant="body2"><strong>Exchange Rate:</strong> {currentOrder.exchange_rate}</Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Box>
            )}
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
            <Button onClick={handleClose} color="primary" variant="contained">
                Close
            </Button>
        </DialogActions>
    </Dialog>

    {/* Update Status Dialog */}
    <Dialog
        open={openStatus}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>Update Order Status</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
            <Box>
                <Grid item xs={12}>
                    <Typography variant="body2" style={{ marginBottom: '16px' }}>
                        <strong>LPO Number:</strong> {currentOrder?.lpo_number}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Status</InputLabel>
                        <Select
                            name="status"
                            value={statusFormData.status}
                            onChange={handleStatusChange}
                            label="Status"
                        >
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="approved">Approved</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Box>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
            <Button onClick={handleClose} color="error" variant="outlined">
                Cancel
            </Button>
            <Button onClick={handleStatusUpdate} color="primary" variant="contained">
                Update Status
            </Button>
        </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
        open={openDelete}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>Confirm Delete</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px', textAlign: 'center' }}>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
                Are you sure you want to delete this order?
            </Typography>
            <Typography variant="h6" color="error" style={{ fontWeight: 600 }}>
                {currentOrder?.lpo_number}
            </Typography>
            <Typography variant="body2" style={{ marginTop: '8px' }}>
                Buyer: {currentOrder?.buyer_name}
            </Typography>
            <Typography variant="body2">
                Total: ${currentOrder?.total_usd}
            </Typography>
            <Typography variant="caption" display="block" style={{ marginTop: '8px' }}>
                This action cannot be undone.
            </Typography>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
            <Button onClick={handleClose} variant="outlined" color="primary">
                Cancel
            </Button>
            <Button onClick={handleDelete} variant="contained" color="error" startIcon={<DeleteIcon />}>
                Delete
            </Button>
        </DialogActions>
    </Dialog>

    {/* Snackbar for notifications */}
    <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
    >
        <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
        >
            {snackbar.message}
        </Alert>
    </Snackbar>
    </div>
);
};

export default Orders;

// Styled components
const StyledTableRow = styled(TableRow)(({ theme }) => ({
'&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
},
'&:hover': {
    backgroundColor: theme.palette.action.selected,
    transition: 'background-color 0.3s ease',
},
}));

const ActionButton = styled(Button)(({ theme }) => ({
margin: theme.spacing(0.5),
minWidth: 'auto',
}));

